import React, { useState, useRef } from 'react'
import {
  StandardButtonBrand,
  StandardButtonSecondary,
} from '../../../ui_reusable/styles/Button.styles'
import { StandardCheckbox } from '../../../ui_reusable/StandardInputs'
import CustomIcons from '../../../ui_reusable/CustomIcons'
import {
  FooterPanel,
  FooterContainer,
  CheckboxGroupContainer,
  ActionSection,
  CheckboxContainer,
  IconWrapper,
  ButtonContainer,
} from '../../comp_reusable/report_page_universal/styles/ReportFooter.styles'
import theme from '../../../../styles/theme'
import { Progress } from 'rsuite'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import { useScenarioReportStore } from '../stores/scenarioReportStore'
import {
  useHasUnsavedChanges,
  type BaseSlice,
} from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import { useNavigate } from 'react-router-dom'

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

interface ScenarioReportFooterProps {
  store?: BaseStoreHook<any>
}

const ScenarioReportFooter: React.FC<ScenarioReportFooterProps> = () => {
  // Local state for UI-only concerns
  const [progress, setProgress] = useState(0)
  const [formats, setFormats] = useState(['pdf'])
  const checkboxRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

  // Get store state and actions
  const exportStatus = useScenarioReportStore((state) => state.exportStatus)
  const unsavedChangesPresent = useHasUnsavedChanges(useScenarioReportStore)
  const savingState = useScenarioReportStore((state) => state.savingState)

  // Get store actions using selectors to avoid unnecessary re-renders
  const handleExport = useScenarioReportStore((state) => state.handleExport)
  const handleSaveReportBtn = useScenarioReportStore(
    (state) => state.handleSaveReportBtn,
  )

  // Access toast notifications and navigation
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()

  /**
   * Handle checkbox state changes for export format selection
   */
  const handleCheckboxChange =
    (format: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setFormats([...formats, format])
      } else {
        setFormats(formats.filter((f) => f !== format))
      }
    }

  /**
   * Handle icon clicks to toggle the corresponding checkbox
   */
  const handleIconClick = (format: string) => () => {
    if (checkboxRefs.current[format]) {
      checkboxRefs.current[format]?.click()
    }
  }

  /**
   * Handle export by calling the store function with required parameters
   */
  const handleExportClick = async () => {
    await handleExport(formats, {
      pushToast,
      setProgress,
    })
  }

  /**
   * Handle the cancel button click - navigates back
   */
  const handleCancel = () => {
    navigate(-1)
  }

  /**
   * Handle save report button click
   */
  const handleSaveReport = async () => {
    await handleSaveReportBtn(pushToast)
  }

  return (
    <FooterPanel>
      <FooterContainer>
        {/* Export format selection and button area */}
        <ActionSection>
          <CheckboxGroupContainer>
            {/* Format selection checkboxes with icons */}
            {[
              { format: 'docx', Icon: CustomIcons.faFileWord, color: 'blue' },
              {
                format: 'pptx',
                Icon: CustomIcons.faFilePowerpoint,
                color: 'orange',
              },
              { format: 'pdf', Icon: CustomIcons.faFilePdf, color: 'red' },
            ].map(({ format, Icon, color }) => (
              <CheckboxContainer key={format}>
                <IconWrapper
                  onClick={handleIconClick(format)}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon size="large" color={color} solid={false} />
                </IconWrapper>
                <StandardCheckbox
                  size="small"
                  checked={formats.includes(format)}
                  onChange={handleCheckboxChange(format)}
                  color={theme.colors.brand}
                  ref={(el) => {
                    checkboxRefs.current[format] = el
                  }}
                />
              </CheckboxContainer>
            ))}
          </CheckboxGroupContainer>
          <StandardButtonBrand
            onClick={handleExportClick}
            disabled={progress > 0}
          >
            {progress > 0 ? `Exporting... ${progress}%` : 'Export'}
          </StandardButtonBrand>
        </ActionSection>

        {/* Progress and status messaging area */}
        <div style={{ width: '20rem' }}>
          {/* Show progress bar during export */}
          {progress > 0 && (
            <Progress
              strokeColor={theme.colors.brand}
              trailColor={theme.colors.primary}
              percent={progress}
              status="active"
              showInfo={false}
            />
          )}
          {/* Show success/error message after export */}
          {exportStatus.show && !progress && (
            <div
              style={{
                color: exportStatus.success ? 'white' : '#FFAAAA',
                backgroundColor: exportStatus.success
                  ? theme.colors.brand
                  : theme.colors.suplementRed,
                textAlign: 'center',
                fontWeight: 'bold',
                opacity: exportStatus.opacity,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              {exportStatus.success
                ? 'Download completed successfully!'
                : 'Something went wrong with the download.'}
            </div>
          )}
        </div>

        {/* Save/cancel buttons */}
        <ButtonContainer>
          <StandardButtonSecondary
            onClick={handleCancel}
            disabled={savingState}
          >
            {unsavedChangesPresent ? 'Cancel' : 'Exit'}
          </StandardButtonSecondary>

          <StandardButtonBrand
            onClick={handleSaveReport}
            disabled={savingState || !unsavedChangesPresent}
            style={{
              opacity: unsavedChangesPresent ? 1 : 0.6,
              cursor: unsavedChangesPresent ? 'pointer' : 'not-allowed',
            }}
          >
            {savingState ? 'Saving...' : 'Save scenario'}
          </StandardButtonBrand>
        </ButtonContainer>
      </FooterContainer>
    </FooterPanel>
  )
}

export default ScenarioReportFooter
